import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Shop() {
  const { user, refreshUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // product_id → qty
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [earnOpen, setEarnOpen] = useState(false);
  const [earnStreak, setEarnStreak] = useState(null);
  const [eggShop, setEggShop] = useState([]);
  const [eggBuyKey, setEggBuyKey] = useState(null);
  // 🏪 General Store — standard + rotation OD-paid digital goods (Feb 2026).
  const [generalStore, setGeneralStore] = useState({ standard: [], rotation: [] });
  const [gsBuyKey, setGsBuyKey] = useState(null);
  const [address, setAddress] = useState({
    name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US",
  });

  async function load() {
    try {
      const { data } = await axios.get(`${API}/shop/products`);
      setProducts(data.products || []);
    } catch (e) {
      setProducts([]);
    }
    try {
      const { data } = await axios.get(`${API}/shop/eggs`);
      setEggShop(data.items || []);
    } catch {
      setEggShop([]);
    }
    try {
      const { data } = await axios.get(`${API}/general-store`);
      setGeneralStore({
        standard: data.standard || [],
        rotation: data.rotation || [],
      });
    } catch {
      setGeneralStore({ standard: [], rotation: [] });
    }
  }
  useEffect(() => { load(); }, []);

  async function buyEgg(item) {
    if (!user) { toast.error("Sign in to buy eggs."); return; }
    if (item.remaining_today <= 0) { toast.error("Sold out for today."); return; }
    if (eggBuyKey) return;
    // 🧾 Roc #38 (Sep 2026) — buying anything requires a confirm.
    const ok = await confirmDialog({
      title: `Buy ${item.name}?`,
      message: `${item.icon || ""} ${item.name} — ${item.price_label || item.price || "cost"}.
This will deduct from your balance immediately.`,
      confirmLabel: "Buy",
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setEggBuyKey(item.key);
    try {
      const { data } = await axios.post(`${API}/shop/eggs/buy`, { item_key: item.key });
      const eggLine = data?.awarded_egg
        ? `${data.awarded_egg.icon} ${data.awarded_egg.rarity_label} egg now incubating in your Nest`
        : `${item.icon} added to inventory`;
      toast.success(`Bought ${item.name} — ${eggLine}`, { duration: 5000 });
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    } finally {
      setEggBuyKey(null);
    }
  }

  // 🏪 Feb 2026 — General Store buy (OD-only, auth-gated).
  async function buyGeneralStoreItem(item) {
    if (!user) { toast.error("Sign in to buy."); return; }
    if (gsBuyKey) return;
    const price = Number(item.price_od || 0);
    if ((user?.omni_dollars || 0) < price) {
      toast.error(`Not enough OD. Need ${price}, you have ${user?.omni_dollars || 0}.`);
      return;
    }
    const ok = await confirmDialog({
      title: `Buy ${item.name}?`,
      message: `${item.icon} ${item.name} — ${price} Ⓞ OD.
${item.description || ""}
OD will be deducted immediately.`,
      confirmLabel: `Buy — ${price} OD`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setGsBuyKey(item.key);
    try {
      const { data } = await axios.post(
        `${API}/general-store/buy`,
        { item_key: item.key },
        { withCredentials: true }
      );
      toast.success(`${item.icon} ${item.name} added to your inventory — ${data.purchased.price_od} OD spent.`, { duration: 5500 });
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    } finally {
      setGsBuyKey(null);
    }
  }

  // Fetch Earn Streak state when user is signed in (Shop is public but the
  // streak card only shows for authed users)
  useEffect(() => {
    if (!user) { setEarnStreak(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/earn-streak/state`, { withCredentials: true });
        if (!cancelled) setEarnStreak(data);
      } catch {
        if (!cancelled) setEarnStreak(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const cartList = products
    .map((p) => ({ ...p, qty: cart[p.product_id] || 0 }))
    .filter((p) => p.qty > 0);
  const totalOD = cartList.reduce((s, p) => s + p.qty * p.price_od, 0);
  const odBalance = user?.omni_dollars || 0;

  function setQty(pid, qty) {
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[pid];
      else next[pid] = qty;
      return next;
    });
  }

  async function placeOrder() {
    if (!user) {
      toast.error("Sign in to check out.");
      return;
    }
    if (totalOD > odBalance) {
      toast.error(`Not enough OD. Need ${totalOD}, you have ${odBalance}.`);
      return;
    }
    const required = ["name", "line1", "city", "state", "postal_code", "country"];
    if (required.some((k) => !(address[k] || "").trim())) {
      toast.error("Fill in all required shipping fields.");
      return;
    }
    // 🧾 Roc #38 — confirm the full-cart purchase before charging OD.
    const totalOD = Object.entries(cart).reduce((s, [pid, qty]) => {
      const p = products.find((x) => x.product_id === pid);
      return s + (p ? p.price_od * qty : 0);
    }, 0);
    const itemCount = Object.values(cart).reduce((s, q) => s + q, 0);
    const ok = await confirmDialog({
      title: "Place order?",
      message: `${itemCount} item${itemCount === 1 ? "" : "s"} · total ${totalOD.toLocaleString()} OD.
Shipping to ${address.name}, ${address.city}, ${address.state} ${address.postal_code}.
OD will be deducted the moment the order is placed.`,
      confirmLabel: `Confirm — ${totalOD.toLocaleString()} OD`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const items = Object.entries(cart).map(([product_id, qty]) => ({ product_id, qty }));
      const { data } = await axios.post(
        `${API}/shop/order`,
        { items, shipping_address: address, origin_url: window.location.origin },
        { withCredentials: true }
      );
      toast.success(`🎁 Order placed! ${data.total_od} OD spent. Order ${data.order_id.slice(0, 12)}…`, { duration: 6000 });
      setCart({});
      setCheckoutOpen(false);
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="shop-page">
      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
              The Phoenix Shop
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white">
              Real Merch · <span className="text-[#D4AF37]">Real Phoenix.</span>
            </h1>
            <p className="text-sm text-neutral-400 mt-2 max-w-xl">
              Pay with your Omni Dollar wallet. Hats, shirts, hoodies, and more. Shipped manually by the founder.
            </p>
          </div>
          {user && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">Your Wallet</div>
              <div className="font-mono tabular-nums font-bold text-3xl text-[#D4AF37]" data-testid="shop-od-balance">
                ☯️ {odBalance.toLocaleString()} <span className="text-xs text-neutral-500">OD</span>
              </div>
              <Link
                to="/profile"
                className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:underline"
              >
                Top up →
              </Link>
            </div>
          )}
        </div>

        {/* How to earn OD — collapsible explainer */}
        <div
          data-testid="earn-od-panel"
          className="mb-8 border border-zinc-900 bg-gradient-to-br from-[#0F0F0F] to-[#160B22]"
        >
          <button
            onClick={() => setEarnOpen((v) => !v)}
            data-testid="earn-od-toggle"
            className="w-full px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors"
            aria-expanded={earnOpen}
          >
            <div className="flex items-center gap-3 text-left">
              <span className="text-2xl">☯️</span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                  How to Earn Omni Dollars
                </div>
                <div className="text-sm text-neutral-300">
                  Three paths: <span className="text-white font-semibold">grind</span>, <span className="text-white font-semibold">convert</span>, or <span className="text-white font-semibold">top up</span>.
                </div>
              </div>
            </div>
            <span className={`text-[#D4AF37] text-xl transition-transform ${earnOpen ? "rotate-180" : ""}`}>⌄</span>
          </button>

          {earnOpen && (
            <div className="px-5 pb-6 pt-2 border-t border-zinc-900 space-y-5 text-sm">
              {/* Path 1 — Convert PT to OD */}
              <div data-testid="earn-path-convert">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-2">
                  Path 1 · Convert Phoenix Tokens
                </div>
                <div className="text-neutral-300 leading-relaxed">
                  Burn <span className="font-mono tabular-nums text-white">500 Phoenix Tokens</span> →
                  mint <span className="font-mono tabular-nums text-[#D4AF37]">1 OD</span>.
                  Daily cap: <span className="font-mono tabular-nums text-white">25 OD</span> per PT day (00:00 PT reset).
                  Available in your Profile · Wallet tab.
                </div>
              </div>

              {/* Path 2 — Grind */}
              <div data-testid="earn-path-grind">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-2">
                  Path 2 · Earn Phoenix Tokens daily
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-neutral-300 font-mono tabular-nums">
                  <li>· Daily sign-in <span className="text-white">+5 Phoenix Tokens</span></li>
                  <li>· Log a workout <span className="text-white">+10 Phoenix Tokens</span></li>
                  <li>· Log a meal <span className="text-white">+10 Phoenix Tokens</span></li>
                  <li>· Generate AI plan <span className="text-white">+25 Phoenix Tokens</span></li>
                  <li>· Daily challenge <span className="text-white">+30 Phoenix Tokens</span></li>
                  <li>· Flip Daily Tip <span className="text-white">+1 Phoenix Tokens</span></li>
                  <li>· Sleep ≥ 7h <span className="text-white">+10 Phoenix Tokens</span></li>
                  <li>· Hit water goal <span className="text-white">+10 Phoenix Tokens</span></li>
                  <li>· Badge unlock <span className="text-white">+25 Phoenix Tokens</span></li>
                  <li>· Spin Wheel jackpot <span className="text-white">up to +250 Phoenix Tokens</span></li>
                </ul>
                <div className="text-xs text-neutral-500 mt-2">
                  A maxed-out day ≈ <span className="text-white font-mono">500 Phoenix Tokens</span> → <span className="text-[#D4AF37] font-mono">1 OD</span>.
                </div>
              </div>

              {/* Path 3 — Top up */}
              <div data-testid="earn-path-topup">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-2">
                  Path 3 · Top up with cash
                </div>
                <div className="grid sm:grid-cols-2 gap-2 text-neutral-300">
                  <div className="bg-[#0A0A0A] border border-zinc-900 px-3 py-2">
                    <span className="text-white font-semibold">Starter Spark</span> · $0.99 →
                    <span className="font-mono text-[#D4AF37]"> 100 OD</span> + 200 Phoenix Tokens bonus
                  </div>
                  <div className="bg-[#0A0A0A] border border-zinc-900 px-3 py-2">
                    <span className="text-white font-semibold">Ember Pack</span> · $9.99 →
                    <span className="font-mono text-[#D4AF37]"> 1,000 OD</span> + 450 Phoenix Tokens
                  </div>
                  <div className="bg-[#0A0A0A] border border-zinc-900 px-3 py-2">
                    <span className="text-white font-semibold">Blaze Pack</span> · $19.99 →
                    <span className="font-mono text-[#D4AF37]"> 2,000 OD</span> + 950 Phoenix Tokens
                  </div>
                  <div className="bg-[#0A0A0A] border border-zinc-900 px-3 py-2">
                    <span className="text-white font-semibold">Phoenix Pack</span> · $99.99 →
                    <span className="font-mono text-[#D4AF37]"> 10,000 OD</span> + 4,500 Phoenix Tokens
                  </div>
                </div>
                <Link
                  to="/profile"
                  data-testid="earn-od-topup-link"
                  className="inline-block mt-3 text-[10px] uppercase tracking-widest text-[#D4AF37] hover:underline"
                >
                  Top up in Profile · Wallet →
                </Link>
              </div>

              <div className="text-[10px] text-neutral-600 uppercase tracking-widest pt-2 border-t border-zinc-900">
                1 OD ≈ $0.01 retail value · daily resets at 00:00 Pacific Time
              </div>
            </div>
          )}
        </div>

        {/* Earn Streak — sign-in loyalty ladder. Authed users only. */}
        {user && earnStreak && (
          <div
            data-testid="earn-streak-panel"
            className="mb-10 border border-[#D4AF37]/30 bg-gradient-to-br from-[#160B22] to-[#0A0A0A] p-5"
          >
            <div className="flex items-baseline justify-between gap-4 mb-3 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🔥</span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                    Your Earn Streak
                  </div>
                  <div className="font-mono tabular-nums text-2xl text-white">
                    <span data-testid="earn-streak-current">{earnStreak.current || 0}</span>
                    <span className="text-xs text-neutral-500 ml-1">days</span>
                  </div>
                </div>
              </div>
              {earnStreak.next ? (
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    Next Milestone
                  </div>
                  <div className="text-sm text-white" data-testid="earn-streak-next">
                    {earnStreak.next.icon} {earnStreak.next.label} ·{" "}
                    <span className="text-[#D4AF37] font-mono">+{earnStreak.next.od} OD</span>
                  </div>
                  <div className="text-[10px] text-neutral-500 font-mono tabular-nums">
                    {earnStreak.next.remaining > 0
                      ? `${earnStreak.next.remaining} day${earnStreak.next.remaining === 1 ? "" : "s"} to go`
                      : "Sign in today to claim"}
                  </div>
                </div>
              ) : (
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                    👑 All Tiers Claimed
                  </div>
                  <div className="text-xs text-neutral-500">Legendary Phoenix. Respect.</div>
                </div>
              )}
            </div>

            {/* Tier ladder */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {(earnStreak.all_tiers || []).map((m) => {
                const claimed = (earnStreak.claimed || []).includes(String(m.days));
                const isNext = earnStreak.next && earnStreak.next.days === m.days;
                return (
                  <div
                    key={m.days}
                    data-testid={`earn-tier-${m.days}`}
                    className={[
                      "p-2 text-center border",
                      claimed
                        ? "bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]"
                        : isNext
                          ? "bg-[#160B22] border-[#D4AF37]/60 text-white ring-1 ring-[#D4AF37]/40"
                          : "bg-[#0A0A0A] border-zinc-900 text-neutral-500",
                    ].join(" ")}
                  >
                    <div className="text-lg leading-none mb-1">{m.icon}</div>
                    <div className="font-mono tabular-nums text-xs">{m.days}d</div>
                    <div className="font-mono tabular-nums text-[10px] mt-0.5">
                      +{m.od} OD
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-neutral-600 uppercase tracking-widest mt-3">
              Bonus OD lands the moment you sign in on each milestone day · 00:00 PT reset
            </div>
          </div>
        )}

        {/* Egg Shop — daily rotating eggs & crowns purchased with OD */}
        {eggShop.length > 0 && (
          <div className="mb-8 border border-[#D4AF37]/40 bg-gradient-to-br from-[#160B22] to-[#0A0A0A]" data-testid="egg-shop">
            <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🪹</span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                    Today's Egg & Crown Drop
                  </div>
                  <div className="text-sm text-neutral-300">
                    Rotates 00:00 PT · pay in <span className="text-[#D4AF37]">Omni Dollars</span>
                  </div>
                </div>
              </div>
              <Link to="/nest" className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:underline" data-testid="egg-shop-nest-link">
                View Nest →
              </Link>
            </div>
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {eggShop.map((it) => {
                const soldOut = it.remaining_today <= 0;
                return (
                  <div
                    key={it.key}
                    data-testid={`egg-shop-${it.key}`}
                    className="bg-[#0A0A0A] border border-zinc-900 p-3 flex flex-col items-center gap-2 relative"
                  >
                    {it.discount_pct > 0 && (
                      <div className="absolute top-2 right-2 bg-[#DC2626] text-white text-[10px] font-bold px-1.5 py-0.5 uppercase tracking-widest">
                        -{it.discount_pct}%
                      </div>
                    )}
                    <div className="text-3xl">{it.icon}</div>
                    <div className="font-display text-base text-white text-center">{it.name}</div>
                    <div className="text-[10px] text-neutral-400 text-center min-h-[2em]">{it.description}</div>
                    <div className="font-mono tabular-nums text-lg text-[#D4AF37]">
                      {it.price_od} OD
                      {it.discount_pct > 0 && (
                        <span className="text-xs text-neutral-500 line-through ml-2">{it.base_price_od}</span>
                      )}
                    </div>
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500">
                      {it.remaining_today} / {it.stock_today} left today
                    </div>
                    <button
                      onClick={() => buyEgg(it)}
                      disabled={soldOut || eggBuyKey === it.key}
                      data-testid={`egg-shop-buy-${it.key}`}
                      className="w-full mt-1 bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-widest text-[10px] py-1.5 disabled:bg-zinc-800 disabled:text-neutral-500 disabled:cursor-not-allowed"
                    >
                      {soldOut ? "Sold Out" : eggBuyKey === it.key ? "Buying…" : "Buy"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 🐉 DragonHeir Dragon Class Store entry — the store itself lives
            on its own route (/dragon-class-store) so the Mage sanctum has
            room to breathe. This is just the doorway from /shop. */}
        <Link
          to="/dragon-class-store"
          data-testid="dragon-class-store-entry"
          className="mb-6 block border border-[#A78BFA]/40 bg-gradient-to-r from-[#1a0c2e] via-[#120820] to-[#0A0A0A] hover:border-[#C4B5FD] transition-colors p-4 flex items-center gap-4"
        >
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/vadpyu2v_wyrmcall-mage-icon.webp"
            alt="Mage Dragon"
            className="w-14 h-14 rounded-full border border-[#A78BFA]/60 object-cover"
            draggable={false}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#A78BFA] font-bold">
              🐉 DragonHeir
            </div>
            <div className="font-display text-lg text-white leading-tight">
              Dragon Class Store
            </div>
            <div className="text-[11px] text-neutral-400 leading-snug">
              Skills for your dragons, paid in Dragon Books. Mage sanctum open · dragons soon.
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#A78BFA] whitespace-nowrap">
            Enter →
          </div>
        </Link>

        {/* 🏪 General Store — OD-paid digital goods (standard + rotation).
            Owner directive Feb 2026: Chit/Nest goods live in the Nest Shop;
            OD goods live here. Rotation sleeve is hidden until stock seeds. */}
        {(generalStore.standard.length > 0 || generalStore.rotation.length > 0) && (
          <div className="mb-8 border border-[#8B5CF6]/40 bg-gradient-to-br from-[#160B22] to-[#0A0A0A]" data-testid="general-store">
            <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🏪</span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#A78BFA] font-bold">
                    General Store
                  </div>
                  <div className="text-sm text-neutral-300">
                    OD-only · <span className="text-[#A78BFA]">standard stock always available</span>
                    {generalStore.rotation.length > 0 && (
                      <> · rotation refreshes 00:00 PT</>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {generalStore.standard.length > 0 && (
              <div className="px-5 pt-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
                  Standard Stock
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {generalStore.standard.map((it) => {
                    const busyBuy = gsBuyKey === it.key;
                    const price = Number(it.price_od || 0);
                    const canAfford = (user?.omni_dollars || 0) >= price;
                    return (
                      <div
                        key={it.key}
                        data-testid={`general-store-${it.key}`}
                        className="bg-[#0A0A0A] border border-zinc-900 hover:border-[#A78BFA]/60 p-4 flex flex-col gap-2 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-3xl leading-none">{it.icon}</div>
                          <span className="text-[9px] uppercase tracking-widest text-[#A78BFA] bg-[#A78BFA]/10 border border-[#A78BFA]/30 px-1.5 py-0.5">
                            Always
                          </span>
                        </div>
                        <div className="font-display text-base text-white leading-tight">{it.name}</div>
                        <div className="text-[11px] text-neutral-400 leading-snug min-h-[3em]">{it.description}</div>
                        <div className="font-mono tabular-nums text-xl text-[#D4AF37] mt-auto">
                          Ⓞ {price.toLocaleString()} <span className="text-xs text-neutral-500">OD</span>
                        </div>
                        <button
                          onClick={() => buyGeneralStoreItem(it)}
                          disabled={busyBuy || !user || !canAfford}
                          data-testid={`general-store-buy-${it.key}`}
                          className="w-full mt-1 bg-[#A78BFA] hover:bg-[#C4B5FD] text-black font-bold uppercase tracking-widest text-[10px] py-2 disabled:bg-zinc-800 disabled:text-neutral-500 disabled:cursor-not-allowed"
                        >
                          {!user ? "Sign in" : !canAfford ? `Need ${(price - (user?.omni_dollars || 0)).toLocaleString()} more OD` : busyBuy ? "Buying…" : "Buy"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {generalStore.rotation.length > 0 && (
              <div className="px-5 pt-4 pb-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
                  Today's Rotation
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {generalStore.rotation.map((it) => {
                    const busyBuy = gsBuyKey === it.key;
                    const price = Number(it.price_od || 0);
                    const canAfford = (user?.omni_dollars || 0) >= price;
                    return (
                      <div
                        key={it.key}
                        data-testid={`general-store-rot-${it.key}`}
                        className="bg-[#0A0A0A] border border-[#D4AF37]/40 hover:border-[#D4AF37] p-4 flex flex-col gap-2 transition-colors relative"
                      >
                        <div className="absolute top-2 right-2 text-[9px] uppercase tracking-widest text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-1.5 py-0.5">
                          Rotation
                        </div>
                        <div className="text-3xl leading-none">{it.icon}</div>
                        <div className="font-display text-base text-white leading-tight">{it.name}</div>
                        <div className="text-[11px] text-neutral-400 leading-snug min-h-[3em]">{it.description}</div>
                        <div className="font-mono tabular-nums text-xl text-[#D4AF37] mt-auto">
                          Ⓞ {price.toLocaleString()} <span className="text-xs text-neutral-500">OD</span>
                        </div>
                        <button
                          onClick={() => buyGeneralStoreItem(it)}
                          disabled={busyBuy || !user || !canAfford}
                          data-testid={`general-store-buy-${it.key}`}
                          className="w-full mt-1 bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-widest text-[10px] py-2 disabled:bg-zinc-800 disabled:text-neutral-500 disabled:cursor-not-allowed"
                        >
                          {!user ? "Sign in" : !canAfford ? `Need ${(price - (user?.omni_dollars || 0)).toLocaleString()} more OD` : busyBuy ? "Buying…" : "Buy"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Products grid */}
        {products.length === 0 ? (
          <p className="text-center text-neutral-500 py-12">No products yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {products.map((p) => {
              const qty = cart[p.product_id] || 0;
              const outOfStock = p.stock <= 0;
              return (
                <div
                  key={p.product_id}
                  data-testid={`product-${p.product_id}`}
                  className="bg-[#0F0F0F] border border-zinc-900 hover:border-[#D4AF37] p-6 transition-colors"
                >
                  <div className="text-6xl mb-3">{p.image}</div>
                  <h3 className="font-display text-xl text-white mb-1">{p.name}</h3>
                  <p className="text-xs text-neutral-500 mb-4 min-h-[36px]">{p.description}</p>
                  <div className="flex items-baseline justify-between mb-4">
                    <div className="font-mono tabular-nums font-bold text-2xl text-[#D4AF37]">
                      {p.price_od.toLocaleString()} <span className="text-xs text-neutral-500">OD</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-600">
                      {outOfStock ? "Sold Out" : `${p.stock} left`}
                    </div>
                  </div>
                  {qty === 0 ? (
                    <button
                      disabled={outOfStock}
                      onClick={() => setQty(p.product_id, 1)}
                      data-testid={`add-${p.product_id}`}
                      className="w-full bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 transition-all disabled:opacity-40"
                    >
                      Add to Cart
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setQty(p.product_id, qty - 1)}
                        className="bg-[#0A0A0A] border border-zinc-700 hover:border-white text-white font-bold w-10 h-10 transition-colors"
                      >
                        −
                      </button>
                      <div className="flex-1 text-center font-mono tabular-nums font-bold text-white">{qty}</div>
                      <button
                        disabled={qty >= p.stock}
                        onClick={() => setQty(p.product_id, qty + 1)}
                        className="bg-[#0A0A0A] border border-zinc-700 hover:border-white text-white font-bold w-10 h-10 transition-colors disabled:opacity-30"
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Cart bar */}
        {cartList.length > 0 && (
          <div
            data-testid="cart-bar"
            className="sticky bottom-4 bg-[#0F0F0F] border-2 border-[#D4AF37] p-4 sm:p-5 flex items-center justify-between gap-4 flex-wrap shadow-2xl"
          >
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                {cartList.reduce((s, p) => s + p.qty, 0)} items
              </div>
              <div className="font-mono tabular-nums font-bold text-2xl text-[#D4AF37]" data-testid="cart-total">
                {totalOD.toLocaleString()} <span className="text-xs text-neutral-500">OD</span>
              </div>
            </div>
            <button
              onClick={() => setCheckoutOpen(true)}
              data-testid="cart-checkout"
              disabled={totalOD > odBalance}
              className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-sm py-3 px-6 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {totalOD > odBalance ? `Need ${totalOD - odBalance} more OD` : "Checkout"}
            </button>
          </div>
        )}

        {/* Checkout modal */}
        {checkoutOpen && (
          <div
            className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => !busy && setCheckoutOpen(false)}
          >
            <div
              className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-lg w-full p-6 sm:p-8 my-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="shop-checkout-modal"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
                Shipping Details
              </div>
              <h2 className="font-display text-2xl text-white mb-4">Where do we send it?</h2>
              <div className="space-y-3 mb-5">
                {[
                  { k: "name", l: "Full Name", w: "" },
                  { k: "line1", l: "Address Line 1", w: "" },
                  { k: "line2", l: "Address Line 2 (optional)", w: "" },
                ].map(({ k, l }) => (
                  <input
                    key={k}
                    data-testid={`addr-${k}`}
                    placeholder={l}
                    value={address[k]}
                    onChange={(e) => setAddress({ ...address, [k]: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] text-white px-3 py-2 text-sm outline-none"
                  />
                ))}
                <div className="grid grid-cols-3 gap-3">
                  <input
                    data-testid="addr-city"
                    placeholder="City"
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    className="col-span-2 bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] text-white px-3 py-2 text-sm outline-none"
                  />
                  <input
                    data-testid="addr-state"
                    placeholder="State"
                    value={address.state}
                    onChange={(e) => setAddress({ ...address, state: e.target.value })}
                    className="bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] text-white px-3 py-2 text-sm outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    data-testid="addr-postal"
                    placeholder="Postal Code"
                    value={address.postal_code}
                    onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
                    className="bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] text-white px-3 py-2 text-sm outline-none"
                  />
                  <input
                    data-testid="addr-country"
                    placeholder="Country (US, CA, ...)"
                    value={address.country}
                    onChange={(e) => setAddress({ ...address, country: e.target.value.toUpperCase() })}
                    className="bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] text-white px-3 py-2 text-sm outline-none uppercase"
                  />
                </div>
              </div>
              <div className="border-t border-zinc-900 pt-4 mb-5 space-y-1">
                {cartList.map((p) => (
                  <div key={p.product_id} className="flex justify-between text-sm">
                    <span className="text-neutral-400">
                      {p.image} {p.name} × {p.qty}
                    </span>
                    <span className="font-mono tabular-nums text-white">
                      {(p.qty * p.price_od).toLocaleString()} OD
                    </span>
                  </div>
                ))}
                <div className="flex justify-between text-base font-bold pt-2 border-t border-zinc-900">
                  <span className="text-white uppercase tracking-widest text-xs">Total</span>
                  <span className="text-[#D4AF37] font-mono">{totalOD.toLocaleString()} OD</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setCheckoutOpen(false)}
                  disabled={busy}
                  className="flex-1 border border-zinc-700 hover:border-white text-white font-bold uppercase tracking-widest text-xs py-3 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={placeOrder}
                  disabled={busy}
                  data-testid="confirm-order"
                  className="flex-1 bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 transition-all disabled:opacity-50"
                >
                  {busy ? "Placing..." : `Pay ${totalOD.toLocaleString()} OD`}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
